import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { STRINGS } from '../constants/strings'
import ConfirmModal from '../components/confirmModal'
import { shareOrigin } from '../utils/shareLink'
import CreateExamForm, { CharCount, FileDropZone, Q_TYPES } from '../components/createExamForm'
import ExamDetail from '../components/examDetail'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks'
import { api } from '../data/api'
import BuyCreditsModal from '../components/buyCreditsModal'

const S = STRINGS.admin

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtDate(iso) {
  return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
}

// ── Toast ─────────────────────────────────────────────────────────────────────

function Toast({ toast }) {
  if (!toast) return null
  const styles = {
    error:   { border: 'border-red-500/40',     text: 'text-red-400',     bg: '#3b0d0d', icon: '✗' },
    info:    { border: 'border-sky-500/40',      text: 'text-sky-300',     bg: '#0c1e2e', icon: '⏳' },
    success: { border: 'border-emerald-500/40',  text: 'text-emerald-300', bg: '#0d2b1f', icon: '✓' },
  }
  const s = styles[toast.type] ?? styles.success
  return (
    <div className={`fixed top-16 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 px-4 py-3 rounded-xl border text-sm shadow-2xl animate-fade-in max-w-sm text-center ${s.border} ${s.text}`}
         style={{ background: s.bg }}>
      <span className="flex-shrink-0">{s.icon}</span>
      <span>{toast.msg}</span>
    </div>
  )
}

// ── Exam List View ────────────────────────────────────────────────────────────

function CopyCodeBadge({ code }) {
  const [copied, setCopied] = useState(false)
  if (!code) return <span className="text-xs" style={{ color: 'var(--text-3)' }}>—</span>

  function handleCopy(e) {
    e.stopPropagation()
    navigator.clipboard.writeText(code).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  return (
    <button
      onClick={handleCopy}
      title="Copy exam code"
      className="flex items-center gap-1.5 group"
    >
      <span className="font-mono text-xs font-bold" style={{ color: copied ? '#34d399' : 'var(--brand)' }}>
        {copied ? '✓ Copied' : code}
      </span>
      <svg viewBox="0 0 16 16" className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
           fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
           style={{ color: 'var(--brand)' }}>
        <rect x="5" y="5" width="9" height="9" rx="1.5"/><path d="M3 11V3a1 1 0 0 1 1-1h8"/>
      </svg>
    </button>
  )
}

const Q_STATUS = {
  pending:    { label: 'No Questions', color: '#94a3b8' },
  generating: { label: 'Generating…',  color: '#fbbf24', spin: true },
  failed:     { label: 'Gen Failed',   color: '#f87171' },
}

function QuestionStatusBadge({ status }) {
  const s = Q_STATUS[status]
  if (!s) return null
  return (
    <span className="inline-flex items-center gap-1 text-[0.6rem] font-semibold px-1.5 py-0.5 rounded-full flex-shrink-0"
      style={{ background: `${s.color}18`, color: s.color, border: `1px solid ${s.color}40` }}>
      {s.spin && <span className="w-1.5 h-1.5 rounded-full border border-current border-t-transparent animate-spin flex-shrink-0" />}
      {s.label}
    </span>
  )
}

// Wording lives in strings.js (S.examCodeMessage) — single source of truth
// shared with ExamCodeChip (components/examDetail.jsx), instead of each
// keeping its own hand-copied template. shareOrigin() always points at the
// branded skillassess.radtar.com on prod; dev keeps its own URL.
function examShareText(exam, orgName) {
  return S.examCodeMessage(exam.name, shareOrigin(), exam.examCode, orgName)
}

function ExamShareButton({ exam, orgName }) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    if (!open) return
    function onClickOutside(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', onClickOutside)
    return () => document.removeEventListener('mousedown', onClickOutside)
  }, [open])

  if (!exam.examCode) return null

  const shareText = examShareText(exam, orgName)
  const waUrl   = `https://wa.me/?text=${encodeURIComponent(shareText)}`
  const mailUrl = `mailto:?subject=${encodeURIComponent(S.examCodeSubject(exam.name))}&body=${encodeURIComponent(shareText)}`

  return (
    <div ref={ref} className="relative">
      <button
        title="Share exam code"
        onClick={() => setOpen(o => !o)}
        className="w-8 h-8 rounded-lg flex items-center justify-center transition-all"
        style={{ color: 'var(--text-3)' }}
        onMouseEnter={e => { e.currentTarget.style.background = 'var(--surface-2)'; e.currentTarget.style.color = 'var(--brand)' }}
        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-3)' }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
          <path d="M8.59 13.51l6.83 3.98M15.41 6.51L8.59 10.49"/>
        </svg>
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 rounded-xl overflow-hidden shadow-2xl z-50 py-1"
             style={{ background: 'var(--surface)', border: '1px solid var(--border)', minWidth: '11rem' }}>
          <a href={waUrl} target="_blank" rel="noreferrer" onClick={() => setOpen(false)}
            className="flex items-center gap-2 px-3 py-2 text-xs font-medium transition-colors"
            style={{ color: 'var(--text-2)', textDecoration: 'none' }}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-2)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="#25d366"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.117.554 4.103 1.523 5.828L.057 23.405a.75.75 0 00.918.918l5.578-1.466A11.942 11.942 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-1.932 0-3.733-.505-5.288-1.388l-.38-.219-3.932 1.034 1.034-3.932-.219-.38A9.96 9.96 0 012 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z"/></svg>
            WhatsApp
          </a>
          <a href={mailUrl} onClick={() => setOpen(false)}
            className="flex items-center gap-2 px-3 py-2 text-xs font-medium transition-colors"
            style={{ color: 'var(--text-2)', textDecoration: 'none' }}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-2)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#f87171" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
            Email
          </a>
        </div>
      )}
    </div>
  )
}

function ExamRow({ exam, onOpen, onDelete, orgName }) {
  const [confirmDel, setConfirmDel] = useState(false)
  const validity = exam.validFrom || exam.validTill
    ? `${exam.validFrom ? fmtDate(exam.validFrom) : '—'} – ${exam.validTill ? fmtDate(exam.validTill) : '—'}`
    : '—'

  return (
    <>
      {confirmDel && (
        <ConfirmModal
          title={S.deleteExamTitle}
          message={S.deleteExamConfirm(exam.name)}
          confirmLabel={S.deleteBtn}
          cancelLabel={S.cancel}
          danger
          onConfirm={() => onDelete(exam.id)}
          onCancel={() => setConfirmDel(false)}
        />
      )}

      {/* Desktop row */}
      <div className="hidden sm:grid px-6 py-4 items-center border-b last:border-0 cursor-pointer transition-colors"
           style={{ gridTemplateColumns: '2fr 100px 1.2fr 0.9fr 90px 110px', borderColor: 'var(--border)' }}
           onClick={() => onOpen(exam)}
           onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-2)'}
           onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center text-sm flex-shrink-0"
               style={{ background: 'rgba(14,165,233,0.1)', border: '1px solid rgba(14,165,233,0.2)', color: 'var(--brand)' }}>
            📝
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 min-w-0">
              <p className="text-sm font-semibold truncate" style={{ color: 'var(--text-1)' }}>{exam.name}</p>
            </div>
            {/* exam.description
              ? <p className="text-xs truncate mt-0.5" style={{ color: 'var(--text-3)' }}>{exam.description}</p>
              : <p className="text-xs italic mt-0.5" style={{ color: 'var(--text-3)' }}>{S.noDescription}</p> */}
          </div>
        </div>
        <div onClick={e => e.stopPropagation()}>
          <CopyCodeBadge code={exam.examCode} />
        </div>
        <p className="text-xs" style={{ color: 'var(--text-3)' }}>{validity}</p>
        <p className="text-xs" style={{ color: 'var(--text-3)' }}>{fmtDate(exam.createdAt)}</p>
        <div className="flex items-center">
          {exam.questionStatus && exam.questionStatus !== 'ready'
            ? <QuestionStatusBadge status={exam.questionStatus} />
            : <span className="inline-flex items-center gap-1 text-[0.65rem] font-semibold px-2 py-0.5 rounded-full"
                style={exam.enabled !== false
                  ? { background: 'rgba(52,211,153,0.12)', color: '#34d399' }
                  : { background: 'rgba(100,116,139,0.15)', color: 'var(--text-3)' }}>
                <span className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                  style={{ background: exam.enabled !== false ? '#34d399' : 'var(--text-3)' }} />
                {exam.enabled !== false ? 'Active' : 'Inactive'}
              </span>
          }
        </div>
        <div className="flex items-center justify-center gap-1" onClick={e => e.stopPropagation()}>
          <ExamShareButton exam={exam} orgName={orgName} />
          <button
            title={S.deleteExamTitle}
            onClick={() => setConfirmDel(true)}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-red-400 hover:bg-red-500/10 transition-all text-sm"
          >✗</button>
        </div>
      </div>

      {/* Mobile row */}
      <div className="sm:hidden px-4 py-4 border-b last:border-0 cursor-pointer transition-colors"
           style={{ borderColor: 'var(--border)' }}
           onClick={() => onOpen(exam)}
           onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-2)'}
           onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center text-sm flex-shrink-0"
               style={{ background: 'rgba(14,165,233,0.1)', border: '1px solid rgba(14,165,233,0.2)' }}>📝</div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 min-w-0 flex-wrap">
              <p className="text-sm font-semibold truncate" style={{ color: 'var(--text-1)' }}>{exam.name}</p>
              {exam.examCode && (
                <span className="flex-shrink-0" onClick={e => e.stopPropagation()}>
                  <CopyCodeBadge code={exam.examCode} />
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
              <p className="text-xs" style={{ color: 'var(--text-3)' }}>{validity} · {fmtDate(exam.createdAt)}</p>
              {exam.questionStatus && exam.questionStatus !== 'ready'
                ? <QuestionStatusBadge status={exam.questionStatus} />
                : <span className="inline-flex items-center gap-1 text-[0.6rem] font-semibold px-1.5 py-0.5 rounded-full"
                    style={exam.enabled !== false
                      ? { background: 'rgba(52,211,153,0.12)', color: '#34d399' }
                      : { background: 'rgba(100,116,139,0.15)', color: 'var(--text-3)' }}>
                    <span className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                      style={{ background: exam.enabled !== false ? '#34d399' : 'var(--text-3)' }} />
                    {exam.enabled !== false ? 'Active' : 'Inactive'}
                  </span>
              }
            </div>
          </div>
          <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
            <ExamShareButton exam={exam} orgName={orgName} />
            <button onClick={() => setConfirmDel(true)}
              className="w-8 h-8 rounded-lg flex items-center justify-center text-red-400 hover:bg-red-500/10 transition-all text-sm">✗</button>
          </div>
        </div>
      </div>
    </>
  )
}

// ── Exam results (org admin view) ────────────────────────────────────────────

const SUBMIT_STATUS = {
  tabSwitch:   { label: '⚠ Tab switch',   color: '#f87171', malpractice: true  },
  screenShare: { label: '⚠ Screen share', color: '#fb923c', malpractice: true  },
  timer:       { label: '⏱ Time expired', color: '#fbbf24', malpractice: false },
  manual:      { label: '✓ Submitted',    color: '#4ade80', malpractice: false },
}

function ExamResults({ exam, showToast }) {
  const { user }                    = useAuth()
  const [attempts, setAttempts]     = useState([])
  const [loading,  setLoading]      = useState(true)
  const [voiding,  setVoiding]      = useState(null) // attemptId being voided

  useEffect(() => {
    api.get(`/api/users/history/${user._id}?examId=${exam.id}`)
      .then(data => setAttempts(data))
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [exam.id])

  async function handleVoid(attemptId) {
    setVoiding(attemptId)
    try {
      await api.patch(`/api/users/attempts/${attemptId}/void`, {})
      setAttempts(prev => prev.map(a => a._id === attemptId ? { ...a, retakeGranted: true } : a))
      showToast('Attempt voided — examinee can retake.')
    } catch (err) {
      showToast(err.message, 'error')
    } finally {
      setVoiding(null) }
  }

  return (
    <section className="card p-6 mt-6">
      <h3 className="text-sm font-bold mb-4" style={{ color: 'var(--text-1)' }}>Examinee Results</h3>

      {loading ? (
        <p className="text-xs" style={{ color: 'var(--text-3)' }}>Loading…</p>
      ) : attempts.length === 0 ? (
        <p className="text-xs" style={{ color: 'var(--text-3)' }}>No attempts yet for this exam.</p>
      ) : (
        <div className="space-y-2">
          {attempts.map(a => {
            const statusKey = a.flags?.autoSubmitted ? (a.flags?.submitReason ?? 'timer') : 'manual'
            const status    = SUBMIT_STATUS[statusKey] ?? SUBMIT_STATUS.manual
            const flagged   = status.malpractice
            const pct     = a.score ?? 0
            const passed  = pct >= 50
            return (
              <div key={a._id}
                className="flex items-center gap-3 px-3 py-2.5 rounded-xl flex-wrap"
                style={{
                  background: a.retakeGranted ? 'var(--surface)' : flagged ? `${status.color}08` : 'var(--surface)',
                  border: `1px solid ${a.retakeGranted || !flagged ? 'var(--border)' : `${status.color}30`}`,
                  opacity: a.retakeGranted ? 0.55 : 1,
                }}>

                {/* User */}
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold truncate" style={{ color: 'var(--text-1)' }}>
                    {a.userId?.name ?? '—'}
                  </p>
                  <p className="text-[0.65rem]" style={{ color: 'var(--text-3)' }}>
                    {a.userId?.email ?? ''}
                  </p>
                </div>

                {/* Score */}
                <span className="text-xs font-bold" style={{ color: passed ? '#4ade80' : '#f87171' }}>
                  {pct}%
                </span>
                <span className="text-[0.65rem] px-1.5 py-0.5 rounded-md font-semibold"
                      style={{ background: passed ? 'rgba(74,222,128,0.1)' : 'rgba(248,113,113,0.1)', color: passed ? '#4ade80' : '#f87171' }}>
                  {passed ? 'PASS' : 'FAIL'}
                </span>

                {/* Date */}
                <span className="text-[0.65rem]" style={{ color: 'var(--text-3)' }}>
                  {new Date(a.submittedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                </span>

                {/* Submit status */}
                {!a.retakeGranted && (
                  <span className="text-[0.65rem] font-semibold px-2 py-0.5 rounded-full"
                        style={{ background: `${status.color}18`, border: `1px solid ${status.color}40`, color: status.color }}>
                    {status.label}
                  </span>
                )}

                {/* Voided badge */}
                {a.retakeGranted && (
                  <span className="text-[0.65rem] font-semibold px-2 py-0.5 rounded-full"
                        style={{ background: 'var(--surface-2)', color: 'var(--text-3)', border: '1px solid var(--border)' }}>
                    Retake Granted
                  </span>
                )}

                {/* Void / retake button */}
                {flagged && !a.retakeGranted && (
                  <button
                    onClick={() => handleVoid(a._id)}
                    disabled={voiding === a._id}
                    className="text-[0.65rem] font-semibold px-2 py-0.5 rounded-lg disabled:opacity-50 transition-all"
                    style={{ background: 'var(--surface-2)', color: 'var(--text-2)', border: '1px solid var(--border)' }}
                  >
                    {voiding === a._id ? '…' : 'Void & Allow Retake'}
                  </button>
                )}
              </div>
            )
          })}
        </div>
      )}
    </section>
  )
}

// ── Exam code chip ────────────────────────────────────────────────────────────


// ── Inline invite code chip ────────────────────────────────────────────────────

function InviteCodeChip({ code, orgName }) {
  const [copied, setCopied] = useState(false)
  if (!code) return null

  function handleCopy() {
    navigator.clipboard.writeText(code).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  // Wording lives in strings.js (S.inviteCodeMessage) — single source of
  // truth shared with superAdminPage.jsx's own duplicate of this chip.
  // shareOrigin() always points at the branded skillassess.radtar.com on
  // prod; dev keeps sharing its own URL (see utils/shareLink.js).
  const shareText = S.inviteCodeMessage(orgName, shareOrigin(), code)
  const waUrl     = `https://wa.me/?text=${encodeURIComponent(shareText)}`
  const mailUrl   = `mailto:?subject=${encodeURIComponent(S.inviteCodeSubject(orgName))}&body=${encodeURIComponent(shareText)}`

  return (
    <div className="mt-3">
      <p className="text-xs text-[var(--text-3)] mb-1.5">Share this code with users — they enter it during registration to access your exams.</p>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs text-[var(--text-3)]">Invite code</span>
        <span className="font-mono text-sm font-bold tracking-widest px-2.5 py-0.5 rounded-lg"
              style={{ background: 'var(--surface-2)', color: 'var(--brand)', border: '1px solid var(--border)' }}>
          {code}
        </span>
        <button
          onClick={handleCopy}
          className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-lg transition-all"
          style={{ background: copied ? 'rgba(52,211,153,0.1)' : 'var(--surface-2)', border: `1px solid ${copied ? 'rgba(52,211,153,0.3)' : 'var(--border)'}`, color: copied ? '#34d399' : 'var(--text-3)' }}
        >
          {copied ? '✓ Copied' : '⎘ Copy'}
        </button>
        <a href={waUrl} target="_blank" rel="noreferrer"
          className="w-6 h-6 rounded-md flex items-center justify-center transition-all"
          style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: '#25d366', textDecoration: 'none' }}
          title="Share via WhatsApp">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.117.554 4.103 1.523 5.828L.057 23.405a.75.75 0 00.918.918l5.578-1.466A11.942 11.942 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-1.932 0-3.733-.505-5.288-1.388l-.38-.219-3.932 1.034 1.034-3.932-.219-.38A9.96 9.96 0 012 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z"/></svg>
        </a>
        <a href={mailUrl}
          className="w-6 h-6 rounded-md flex items-center justify-center transition-all"
          style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: '#f87171', textDecoration: 'none' }}
          title="Share via Email">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
        </a>
      </div>
    </div>
  )
}

// ── Main AdminPage ────────────────────────────────────────────────────────────

// Normalize MongoDB _id → id so the rest of the page can use exam.id uniformly
function normalizeExam(e) {
  return { ...e, id: e._id?.toString() ?? e.id }
}

const SORT_OPTIONS = [
  { value: 'newest',   label: S.sortNewest  },
  { value: 'oldest',   label: S.sortOldest  },
  { value: 'az',       label: S.sortAZ      },
  { value: 'za',       label: S.sortZA      },
  { value: 'mostpdfs', label: S.sortMostPdfs },
]

function sortExams(exams, sort) {
  return [...exams].sort((a, b) => {
    if (sort === 'oldest')   return new Date(a.createdAt) - new Date(b.createdAt)
    if (sort === 'az')       return a.name.localeCompare(b.name)
    if (sort === 'za')       return b.name.localeCompare(a.name)
    if (sort === 'mostpdfs') return (b.textbookSources.length + b.patternSources.length) - (a.textbookSources.length + a.patternSources.length)
    return new Date(b.createdAt) - new Date(a.createdAt)
  })
}

// ── Users view ────────────────────────────────────────────────────────────────

const TRIAL_LEVELS_ORDER = ['Beginner', 'Intermediate', 'Advanced', 'Expert']

// How many Trial (practice-mode) attempts this examinee has made per
// difficulty level, grouped by exam — answers "how many times did they try
// each level" without having to count rows in the list below by hand.
function trialAttemptSummary(history) {
  const byExam = new Map() // examName -> { Beginner: n, Intermediate: n, ... }
  for (const h of history) {
    if (h.mode !== 'practice' || !h.difficulty) continue
    if (!byExam.has(h.examName)) byExam.set(h.examName, Object.fromEntries(TRIAL_LEVELS_ORDER.map(l => [l, 0])))
    const counts = byExam.get(h.examName)
    if (h.difficulty in counts) counts[h.difficulty] += 1
  }
  return [...byExam.entries()].filter(([, counts]) => Object.values(counts).some(n => n > 0))
}

function UserHistoryModal({ user, onClose }) {
  const [history, setHistory] = useState(null)

  useEffect(() => {
    getUserHistory(user.id).then(setHistory)
  }, [user.id])

  const trialSummary = history ? trialAttemptSummary(history) : []

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center px-4"
         style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(6px)' }}
         onClick={onClose}>
      <div className="w-full max-w-lg rounded-2xl overflow-hidden animate-fade-up"
           style={{ background: 'var(--surface)', border: '1px solid var(--border)', boxShadow: '0 24px 64px rgba(0,0,0,0.5)' }}
           onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="px-6 py-4 border-b flex items-center justify-between" style={{ borderColor: 'var(--border)' }}>
          <div>
            <h3 className="text-base font-bold" style={{ color: 'var(--text-1)' }}>{user.name}</h3>
            <p className="text-xs" style={{ color: 'var(--text-3)' }}>{user.email}</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center text-sm transition"
            style={{ color: 'var(--text-3)' }}
            onMouseEnter={e => { e.currentTarget.style.background = 'var(--surface-2)'; e.currentTarget.style.color = 'var(--text-1)' }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-3)' }}
          >✕</button>
        </div>

        {/* History */}
        <div className="p-4 max-h-96 overflow-y-auto">
          {!history ? (
            <p className="text-sm text-center py-6" style={{ color: 'var(--text-3)' }}>Loading…</p>
          ) : history.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-2xl mb-2">📭</p>
              <p className="text-sm" style={{ color: 'var(--text-3)' }}>No exam attempts yet.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {trialSummary.length > 0 && (
                <div className="space-y-2 mb-3">
                  <p className="text-[0.65rem] font-bold uppercase tracking-widest" style={{ color: 'var(--text-3)' }}>Trial Attempts</p>
                  {trialSummary.map(([examName, counts]) => (
                    <div key={examName} className="card p-3">
                      <p className="text-xs font-semibold truncate mb-1.5" style={{ color: 'var(--text-1)' }}>{examName}</p>
                      <div className="grid grid-cols-4 gap-1.5">
                        {TRIAL_LEVELS_ORDER.map(level => (
                          <div key={level} className="text-center px-1.5 py-1 rounded-lg"
                               style={{ background: 'var(--chip-bg)', border: '1px solid var(--chip-border)' }}>
                            <p className="text-sm font-bold" style={{ color: counts[level] > 0 ? 'var(--brand)' : 'var(--text-3)' }}>{counts[level]}</p>
                            <p className="text-[0.55rem] truncate" style={{ color: 'var(--text-3)' }}>{level}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {history.map(h => {
                const pct = Math.round((h.score / h.total) * 100)
                return (
                  <div key={h.id} className="card p-4 flex items-center gap-4">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center text-sm font-bold flex-shrink-0"
                      style={{ background: h.passed ? 'rgba(52,211,153,0.12)' : 'rgba(248,113,113,0.12)', color: h.passed ? '#34d399' : '#f87171' }}>
                      {pct}%
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold truncate" style={{ color: 'var(--text-1)' }}>{h.examName}</p>
                      <p className="text-[0.65rem] mt-0.5" style={{ color: 'var(--text-3)' }}>
                        {h.mode === 'virtual' ? `⏱ ${STRINGS.modes.virtual}` : `📖 ${STRINGS.modes.practice}`} · {h.difficulty} · {fmtDate(h.takenAt)}
                      </p>
                    </div>
                    <span className="text-xs font-semibold px-2 py-0.5 rounded-lg flex-shrink-0"
                      style={{ background: h.passed ? 'rgba(52,211,153,0.12)' : 'rgba(248,113,113,0.12)', color: h.passed ? '#34d399' : '#f87171' }}>
                      {h.passed ? 'Pass' : 'Fail'}
                    </span>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  )
}

function UsersView({ adminId, showToast }) {
  const navigate     = useNavigate()
  const [users,      setUsers]      = useState(null)
  const [total,      setTotal]      = useState(0)
  const [totalPages, setTotalPages] = useState(1)
  const [stats,      setStats]      = useState({})
  const [removeUser,  setRemoveUser]  = useState(null)
  const [approveUser, setApproveUser] = useState(null)
  const [extendUser,  setExtendUser]  = useState(null)
  const [extendDays,  setExtendDays]  = useState('60')
  const [search,      setSearch]      = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [page,        setPage]        = useState(1)
  const [perPage,     setPerPage]     = useState(10)

  function fetchUsers(pg = page, limit = perPage, q = debouncedSearch) {
    if (!adminId) return
    setUsers(null)
    const params = new URLSearchParams({ page: pg, limit, ...(q ? { search: q } : {}) })
    api.get(`/api/orgs/${adminId}/users?${params}`)
      .then(data => { setUsers(data.users); setTotal(data.total); setTotalPages(data.totalPages) })
      .catch(() => { setUsers([]); setTotal(0); setTotalPages(1) })
  }

  // Debounce the typed value before it ever reaches fetchUsers — without
  // this, every keystroke fired its own request, and slower/earlier ones
  // could resolve after later ones, flickering the list back to a
  // single-letter search result after more had already been typed.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 400)
    return () => clearTimeout(t)
  }, [search])

  useEffect(() => { setPage(1); fetchUsers(1, perPage, debouncedSearch) }, [debouncedSearch])

  useEffect(() => { fetchUsers() }, [adminId])

  async function handleApprove() {
    const u = approveUser
    setApproveUser(null)
    try {
      await api.patch(`/api/orgs/${adminId}/users/${u._id}/approve`)
      showToast(`${u.name} approved.`)
      fetchUsers()
    } catch {
      showToast('Failed to approve user.', 'error')
    }
  }

  async function handleRemove() {
    const u = removeUser
    setRemoveUser(null)
    try {
      await api.delete(`/api/orgs/${adminId}/users/${u._id}`)
      showToast(`${u.name} removed from organisation.`)
      fetchUsers()
    } catch {
      showToast('Failed to remove user.', 'error')
    }
  }

  async function handleExtend() {
    const u    = extendUser
    const days = parseInt(extendDays)
    setExtendUser(null)
    if (!days || days <= 0) return
    try {
      const updated = await api.patch(`/api/orgs/${adminId}/users/${u._id}/extend-expiry`, { days })
      showToast(`${u.name}'s account extended by ${days} day${days !== 1 ? 's' : ''}.`)
      setUsers(prev => prev.map(x => x._id === u._id ? { ...x, accountExpiresAt: updated.accountExpiresAt } : x))
    } catch {
      showToast('Failed to extend account.', 'error')
    }
  }

  function ValidityBadge({ expiresAt }) {
    if (!expiresAt) return <span className="text-xs" style={{ color: 'var(--text-3)' }}>—</span>
    const now      = new Date()
    const exp      = new Date(expiresAt)
    const daysLeft = Math.ceil((exp - now) / 86400000)
    const color    = daysLeft <= 0 ? '#f87171' : daysLeft <= 30 ? '#fbbf24' : '#34d399'
    return <span className="text-xs font-medium" style={{ color }}>{fmtDate(expiresAt)}</span>
  }

  if (!users) return <div className="card p-6 text-sm text-[var(--text-3)]">Loading…</div>

  const pageItems = users
  const safePage  = page

  return (
    <>
      {approveUser && (
        <ConfirmModal
          title="Approve User"
          message={`Approve ${approveUser.name}? They will be able to log in and access your exams.`}
          confirmLabel="Approve"
          onConfirm={handleApprove}
          onCancel={() => setApproveUser(null)}
          success
        />
      )}
      {removeUser && (
        <ConfirmModal
          title="Remove User"
          message={`Remove ${removeUser.name} from your organisation? They will lose access to your exams.`}
          confirmLabel="Remove"
          onConfirm={handleRemove}
          onCancel={() => setRemoveUser(null)}
          danger
        />
      )}
      {extendUser && (() => {
        const days    = parseInt(extendDays) || 0
        const now     = new Date()
        const current = extendUser.accountExpiresAt ? new Date(extendUser.accountExpiresAt) : null
        const isValid = current && current > now
        const base    = isValid ? current : now
        const newExp  = days > 0 ? new Date(base.getTime() + days * 86400000) : null
        return createPortal(
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className="card p-6 w-84 shadow-2xl animate-fade-up" style={{ minWidth: '22rem' }}>
              <p className="text-xs font-semibold uppercase tracking-widest mb-3" style={{ color: 'var(--brand)' }}>Extend Account</p>
              <p className="text-sm font-semibold mb-3" style={{ color: 'var(--text-1)' }}>{extendUser.name}</p>

              {/* Current expiry info */}
              <div className="rounded-xl px-3 py-2.5 mb-4 text-xs space-y-1" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)' }}>
                <div className="flex justify-between">
                  <span style={{ color: 'var(--text-3)' }}>Current expiry</span>
                  <span className="font-semibold" style={{ color: current ? (isValid ? '#34d399' : '#f87171') : 'var(--text-3)' }}>
                    {current ? fmtDate(current) : '—'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span style={{ color: 'var(--text-3)' }}>Extending from</span>
                  <span className="font-medium" style={{ color: 'var(--text-2)' }}>
                    {isValid ? `Expiry date` : `Today (${fmtDate(now)})`}
                  </span>
                </div>
                <div className="flex justify-between border-t pt-1" style={{ borderColor: 'var(--border)' }}>
                  <span style={{ color: 'var(--text-3)' }}>New expiry</span>
                  <span className="font-bold" style={{ color: newExp ? 'var(--brand)' : 'var(--text-3)' }}>
                    {newExp ? fmtDate(newExp) : '—'}
                  </span>
                </div>
              </div>

              <label className="text-xs font-medium block mb-1" style={{ color: 'var(--text-2)' }}>Days to extend</label>
              <input
                type="number" min="1" max="3650"
                value={extendDays}
                onChange={e => setExtendDays(e.target.value)}
                className="w-full px-3 py-2 rounded-xl text-sm outline-none mb-4"
                style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text-1)' }}
                onFocus={e => e.currentTarget.style.borderColor = 'var(--brand)'}
                onBlur={e  => e.currentTarget.style.borderColor = 'var(--border)'}
                autoFocus
              />
              <div className="flex gap-2">
                <button onClick={() => setExtendUser(null)}
                  className="flex-1 py-2 rounded-xl text-sm font-semibold transition"
                  style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text-2)' }}>
                  Cancel
                </button>
                <button onClick={handleExtend} disabled={!newExp}
                  className="flex-1 py-2 rounded-xl text-sm font-semibold transition disabled:opacity-40"
                  style={{ background: 'var(--brand)', color: '#fff' }}>
                  Extend
                </button>
              </div>
            </div>
          </div>,
          document.body
        )
      })()}

      {total === 0 && !search ? (
        <div className="card p-10 text-center">
          <p className="text-4xl mb-3">👥</p>
          <p className="text-base font-semibold mb-1" style={{ color: 'var(--text-1)' }}>No users yet</p>
          <p className="text-sm" style={{ color: 'var(--text-3)' }}>Share your invite code for users to join your organisation.</p>
        </div>
      ) : (
        <>
          {/* Search */}
          <div className="mb-4 flex flex-wrap items-center gap-3">
            <div className="relative w-full sm:w-56">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm" style={{ color: 'var(--text-3)' }}>🔍</span>
              <input type="text" value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search..."
                className="w-full pl-9 pr-4 py-2.5 rounded-xl text-sm outline-none transition-all"
                style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-1)' }}
                onFocus={e => e.currentTarget.style.borderColor = 'var(--brand)'}
                onBlur={e  => e.currentTarget.style.borderColor = 'var(--border)'} />
              {search && (
                <button onClick={() => setSearch('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-xs" style={{ color: 'var(--text-3)' }}>✕</button>
              )}
            </div>
            <span className="text-xs" style={{ color: 'var(--text-3)' }}>{total} result{total !== 1 ? 's' : ''}</span>
            <div className="flex items-center gap-2 ml-auto">
              <span className="text-xs" style={{ color: 'var(--text-3)' }}>Rows per page</span>
              <select value={perPage} onChange={e => {
                const l = Number(e.target.value); setPerPage(l); setPage(1); fetchUsers(1, l)
              }}
                className="text-xs rounded-lg px-2 py-1.5 outline-none cursor-pointer"
                style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-1)' }}>
                {[5, 10, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
          </div>

        <div className="card overflow-hidden">
          {/* Header */}
          <div className="hidden sm:grid px-6 py-3.5 text-xs font-bold uppercase tracking-widest border-b"
               style={{ gridTemplateColumns: '2fr 1.2fr 1.2fr 0.8fr 0.8fr 1.2fr 1.2fr', color: 'var(--text-3)', borderColor: 'var(--border)', background: 'var(--surface-2)' }}>
            <span>User</span>
            <span>Joined</span>
            <span>Mobile</span>
            <span className="text-center">Exams Taken</span>
            {/* <span className="text-center">{STRINGS.modes.practice}</span> */}
            <span className="text-center">Status</span>
            <span>Valid Till</span>
            <span className="text-center">Actions</span>
          </div>

          {pageItems.map(u => {
            const approved = u.status === 'approved'
            const mobile = u.phone?.number ? `${u.phone.countryCode} ${u.phone.number}` : '—'
            return (
            <div key={u._id}
                 className="hidden sm:grid px-6 py-4 items-center border-b last:border-0 transition-colors"
                 style={{ gridTemplateColumns: '2fr 1.2fr 1.2fr 0.8fr 0.8fr 1.2fr 1.2fr', borderColor: 'var(--border)' }}
                 onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-2)'}
                 onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              {/* User */}
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold flex-shrink-0"
                     style={{ background: 'var(--brand)', color: '#fff' }}>
                  {u.name?.[0]?.toUpperCase() ?? '?'}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold truncate" style={{ color: 'var(--text-1)' }}>{u.name}</p>
                  <p className="text-xs truncate" style={{ color: 'var(--text-3)' }}>{u.email}</p>
                </div>
              </div>
              {/* Joined */}
              <p className="text-xs" style={{ color: 'var(--text-3)' }}>{fmtDate(u.createdAt)}</p>
              {/* Mobile */}
              <p className="text-xs" style={{ color: 'var(--text-3)' }}>{mobile}</p>
              {/* Exams Taken */}
              <p className="text-sm font-bold text-center" style={{ color: 'var(--text-1)' }}>
                {approved ? (u.examsTaken ?? 0) : '—'}
              </p>
              {/* Practice */}
              {/* <p className="text-sm font-bold text-center" style={{ color: 'var(--text-1)' }}>
                {approved ? (stats[u._id]?.practice ?? '—') : '—'}
              </p> */}
              {/* Status */}
              <div className="flex justify-center">
                <span className="inline-flex items-center gap-1 text-[0.65rem] font-semibold px-2 py-0.5 rounded-full"
                  style={approved
                    ? { background: 'rgba(52,211,153,0.12)', color: '#34d399' }
                    : { background: 'rgba(251,191,36,0.12)', color: '#fbbf24' }}>
                  <span className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                    style={{ background: approved ? '#34d399' : '#fbbf24' }} />
                  {approved ? 'Active' : 'Pending'}
                </span>
              </div>
              {/* Validity */}
              <div><ValidityBadge expiresAt={u.accountExpiresAt} /></div>
              {/* Actions */}
              <div className="flex items-center justify-center gap-1.5">
                {!approved && (
                  <button onClick={() => setApproveUser(u)}
                    className="w-7 h-7 rounded-lg flex items-center justify-center transition"
                    style={{ color: '#34d399', border: '1px solid rgba(52,211,153,0.3)', background: 'rgba(52,211,153,0.08)' }}
                    title="Approve user"
                    onMouseEnter={e => { e.currentTarget.style.background = 'rgba(52,211,153,0.18)' }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'rgba(52,211,153,0.08)' }}>
                    <svg viewBox="0 0 16 16" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M2.5 8.5l3.5 3.5 7-7" />
                    </svg>
                  </button>
                )}
                {approved && (
                  <button onClick={() => { setExtendUser(u); setExtendDays('60') }}
                    className="w-7 h-7 rounded-lg flex items-center justify-center transition"
                    style={{ color: 'var(--text-3)', border: '1px solid var(--border)' }}
                    title="Extend account validity"
                    onMouseEnter={e => { e.currentTarget.style.borderColor = '#fbbf24'; e.currentTarget.style.color = '#fbbf24' }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-3)' }}>
                    <svg viewBox="0 0 16 16" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="8" cy="8" r="6"/><path d="M8 5v3l2 1.5"/><path d="M11.5 1.5l2 2-2 2"/><path d="M13.5 3.5H11"/>
                    </svg>
                  </button>
                )}
                {approved && (
                  <button onClick={() => navigate('/history', { state: { userId: u._id, userName: u.name } })}
                    className="w-7 h-7 rounded-lg flex items-center justify-center transition"
                    style={{ color: 'var(--text-3)', border: '1px solid var(--border)' }}
                    title="View exam history"
                    onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--brand)'; e.currentTarget.style.color = 'var(--brand)' }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-3)' }}>
                    <svg viewBox="0 0 16 16" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 8s2.5-5 7-5 7 5 7 5-2.5 5-7 5-7-5-7-5z"/><circle cx="8" cy="8" r="2"/>
                    </svg>
                  </button>
                )}
                <button onClick={() => setRemoveUser(u)}
                  className="w-7 h-7 rounded-lg flex items-center justify-center transition text-sm"
                  style={{ color: '#f87171', border: '1px solid rgba(248,113,113,0.3)', background: 'rgba(248,113,113,0.08)' }}
                  title="Remove user"
                  onMouseEnter={e => { e.currentTarget.style.background = 'rgba(248,113,113,0.18)' }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'rgba(248,113,113,0.08)' }}>
                  ✗
                </button>
              </div>
            </div>
            )
          })}

          {/* Mobile cards */}
          {pageItems.map(u => {
            const approved = u.status === 'approved'
            const mobile = u.phone?.number ? `${u.phone.countryCode} ${u.phone.number}` : null
            return (
            <div key={`m-${u._id}`} className="sm:hidden px-4 py-4 border-b last:border-0" style={{ borderColor: 'var(--border)' }}>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold flex-shrink-0"
                     style={{ background: 'var(--brand)', color: '#fff' }}>
                  {u.name?.[0]?.toUpperCase() ?? '?'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold truncate" style={{ color: 'var(--text-1)' }}>{u.name}</p>
                  <p className="text-xs truncate" style={{ color: 'var(--text-3)' }}>{u.email}</p>
                </div>
                <div className="flex items-center gap-1.5">
                  {!approved && (
                    <button onClick={() => setApproveUser(u)}
                      className="w-7 h-7 rounded-lg flex items-center justify-center transition"
                      style={{ color: '#34d399', border: '1px solid rgba(52,211,153,0.3)', background: 'rgba(52,211,153,0.08)' }}
                      title="Approve"
                      onMouseEnter={e => { e.currentTarget.style.background = 'rgba(52,211,153,0.18)' }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'rgba(52,211,153,0.08)' }}>
                      <svg viewBox="0 0 16 16" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M2.5 8.5l3.5 3.5 7-7" />
                      </svg>
                    </button>
                  )}
                  {approved && (
                    <button onClick={() => { setExtendUser(u); setExtendDays('60') }}
                      className="w-7 h-7 rounded-lg flex items-center justify-center transition"
                      style={{ color: 'var(--text-3)', border: '1px solid var(--border)' }}
                      title="Extend account validity"
                      onMouseEnter={e => { e.currentTarget.style.borderColor = '#fbbf24'; e.currentTarget.style.color = '#fbbf24' }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-3)' }}>
                      <svg viewBox="0 0 16 16" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="8" cy="8" r="6"/><path d="M8 5v3l2 1.5"/><path d="M11.5 1.5l2 2-2 2"/><path d="M13.5 3.5H11"/>
                      </svg>
                    </button>
                  )}
                  {approved && (
                    <button onClick={() => navigate('/history', { state: { userId: u._id, userName: u.name } })}
                      className="w-7 h-7 rounded-lg flex items-center justify-center transition"
                      style={{ color: 'var(--text-3)', border: '1px solid var(--border)' }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--brand)'; e.currentTarget.style.color = 'var(--brand)' }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-3)' }}>
                      <svg viewBox="0 0 16 16" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M1 8s2.5-5 7-5 7 5 7 5-2.5 5-7 5-7-5-7-5z"/><circle cx="8" cy="8" r="2"/>
                      </svg>
                    </button>
                  )}
                  <button onClick={() => setRemoveUser(u)}
                    className="w-7 h-7 rounded-lg flex items-center justify-center transition"
                    style={{ color: '#f87171', border: '1px solid rgba(248,113,113,0.3)', background: 'rgba(248,113,113,0.08)' }}
                    title="Remove user"
                    onMouseEnter={e => { e.currentTarget.style.background = 'rgba(248,113,113,0.18)' }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'rgba(248,113,113,0.08)' }}>
                    ✗
                  </button>
                </div>
              </div>
              <div className="flex gap-3 text-xs items-center flex-wrap" style={{ color: 'var(--text-3)' }}>
                <span className="inline-flex items-center gap-1 font-semibold px-2 py-0.5 rounded-full text-[0.65rem]"
                  style={approved
                    ? { background: 'rgba(52,211,153,0.12)', color: '#34d399' }
                    : { background: 'rgba(251,191,36,0.12)', color: '#fbbf24' }}>
                  {approved ? 'Active' : 'Pending'}
                </span>
                {mobile && <span>📱 {mobile}</span>}
                <span>Joined {fmtDate(u.createdAt)}</span>
                {u.accountExpiresAt && <ValidityBadge expiresAt={u.accountExpiresAt} />}
              </div>
            </div>
            )
          })}
        </div>

          {totalPages > 1 && (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-5">
              <p className="text-xs" style={{ color: 'var(--text-3)' }}>
                Showing {(safePage - 1) * perPage + 1}–{Math.min(safePage * perPage, total)} of {total}
              </p>
              <div className="flex flex-wrap items-center gap-1">
                <button onClick={() => { const p = Math.max(1, page - 1); setPage(p); fetchUsers(p) }} disabled={safePage === 1}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all disabled:opacity-30"
                  style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-2)' }}>← Prev</button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(n => (
                  <button key={n} onClick={() => { setPage(n); fetchUsers(n) }}
                    className="w-8 h-8 rounded-lg text-xs font-semibold transition-all"
                    style={n === safePage
                      ? { background: 'var(--brand)', color: '#fff' }
                      : { background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-2)' }}>
                    {n}
                  </button>
                ))}
                <button onClick={() => { const p = Math.min(totalPages, page + 1); setPage(p); fetchUsers(p) }} disabled={safePage === totalPages}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all disabled:opacity-30"
                  style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-2)' }}>Next →</button>
              </div>
            </div>
          )}
        </>
      )}
    </>
  )
}

export default function AdminPage() {
  const { user, updateUser }        = useAuth()
  const location                    = useLocation()
  const navigate                    = useNavigate()
  const [tab, setTab]               = useState(location.state?.tab ?? 'exams')
  const [exams, setExams]           = useState([])
  const [examMeta, setExamMeta]     = useState({ total: 0, pages: 1 })
  const [loading, setLoading]       = useState(true)
  const [selectedExam, setSelected] = useState(null)
  const [creating, setCreating]     = useState(false)
  const [creditsBlockedMsg, setCreditsBlockedMsg] = useState(null)
  const [buyCreditsOpen, setBuyCreditsOpen] = useState(false)
  const [saving, setSaving]         = useState(false)
  const [toast, setToast]           = useState(null)
  const [search, setSearch]         = useState('')
  const [debouncedQ, setDebouncedQ] = useState('')
  const [sort, setSort]             = useState('newest')
  const [examPage, setExamPage]     = useState(1)
  const [examPerPage, setExamPerPage] = useState(10)
  const examParamsRef = useRef({ page: 1, limit: 10, q: '', sort: 'newest' })

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type })
    setTimeout(() => setToast(null), 3500)
  }

  // Checked fresh (not from the possibly-stale cached auth user) right when the
  // examiner asks to create an exam — catches an exhausted balance before they
  // spend time filling out the form, instead of only failing on submit.
  async function handleNewExamClick() {
    try {
      const me = await api.get('/api/users/me')
      if ((me.creditsBalance ?? 0) <= 0) {
        setCreditsBlockedMsg('Your organisation has run out of credits. Buy credits to continue.')
        return
      }
    } catch { /* fail open — the real gate still runs server-side on submit */ }
    setCreating(true)
  }

  // Debounce search → debouncedQ
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(search), 400)
    return () => clearTimeout(t)
  }, [search])

  // Reset to page 1 when search/sort/perPage changes
  useEffect(() => { setExamPage(1) }, [debouncedQ, sort, examPerPage])

  // Backend fetch
  useEffect(() => {
    const params = { page: examPage, limit: examPerPage, q: debouncedQ, sort }
    examParamsRef.current = params
    setLoading(true)
    const qs = new URLSearchParams({ page: examPage, limit: examPerPage, sort, ...(debouncedQ ? { q: debouncedQ } : {}) })
    api.get(`/api/exams?${qs}`)
      .then(data => { setExams(data.exams.map(normalizeExam)); setExamMeta({ total: data.total, pages: data.pages }); setLoading(false) })
      .catch(() => setLoading(false))
  }, [examPage, examPerPage, debouncedQ, sort])

  function refetchExams() {
    const { page, limit, q, sort: srt } = examParamsRef.current
    const qs = new URLSearchParams({ page, limit, sort: srt, ...(q ? { q } : {}) })
    api.get(`/api/exams?${qs}`)
      .then(data => { setExams(data.exams.map(normalizeExam)); setExamMeta({ total: data.total, pages: data.pages }) })
      .catch(() => {})
  }

  // Poll every 10s while any exam is generating questions
  useEffect(() => {
    const hasGenerating = exams.some(e => e.questionStatus === 'generating')
    if (!hasGenerating) return
    const id = setInterval(refetchExams, 10_000)
    return () => clearInterval(id)
  }, [exams])

  async function handleCreate({ name, description, textbookFiles, patternFiles, questionSettings, difficulty, bloomEnabled, bloomDistribution, bloomMarks, practiceMultiplier, trialsEnabled, trialLevelLockEnabled, showQuestionTypeIndicator, pageRanges, validFrom, validTill }) {
    // Check affordability using the actual picked files' sizes before creating
    // anything — avoids a wasted create+upload round-trip if this obviously
    // exceeds what the organisation can afford.
    const totalSizeBytes = [...textbookFiles, ...patternFiles].reduce((sum, f) => sum + f.size, 0)
    try {
      const check = await api.post('/api/exams/estimate-cost', { totalSizeBytes })
      if (check.blocked) {
        setCreditsBlockedMsg(check.message)
        return
      }
    } catch { /* fail open — the real gate still runs server-side on generate */ }

    setSaving(true)
    let examId = null
    try {
      const raw  = await api.post('/api/exams', { name, description, questionSettings, difficulty, bloomEnabled, bloomDistribution, bloomMarks, practiceMultiplier, trialsEnabled, trialLevelLockEnabled, showQuestionTypeIndicator, pageRanges, validFrom, validTill })
      const exam = normalizeExam(raw)
      examId = exam.id

      for (const file of textbookFiles) {
        const { uploadUrl, filePath } = await api.post('/api/upload/signed-url', {
          type: 'examPdf', examId: exam.id, filename: file.name, contentType: 'application/pdf',
        })
        await api.putFile(uploadUrl, file, null, { contentType: 'application/pdf' })
        const { source } = await api.post(`/api/exams/${exam.id}/confirm-pdf`, {
          filePath, name: file.name, sourceKey: 'textbookSources', size: file.size,
        })
        exam.textbookSources = [...(exam.textbookSources ?? []), source]
      }

      for (const file of patternFiles) {
        const { uploadUrl, filePath } = await api.post('/api/upload/signed-url', {
          type: 'examPdf', examId: exam.id, filename: file.name, contentType: 'application/pdf',
        })
        await api.putFile(uploadUrl, file, null, { contentType: 'application/pdf' })
        const { source } = await api.post(`/api/exams/${exam.id}/confirm-pdf`, {
          filePath, name: file.name, sourceKey: 'patternSources', size: file.size,
        })
        exam.patternSources = [...(exam.patternSources ?? []), source]
      }

      if (textbookFiles.length) {
        await api.post(`/api/exams/${exam.id}/generate`)
        exam.questionStatus = 'generating'
      }

      refetchExams()
      setCreating(false)
      showToast('Exam created — questions are being generated. We\'ll notify you via email once ready.', 'info')
    } catch (err) {
      // Creation partially failed after the exam record was already inserted (e.g. a PDF
      // upload dropped mid-flight) — remove it rather than leaving a stuck, PDF-less orphan
      // with questionStatus permanently 'pending'.
      if (examId) {
        try { await api.delete(`/api/exams/${examId}`) } catch { /* best-effort cleanup */ }
      }
      // Same "buy credits" prompt as the estimate-cost check above and the
      // locked-toggle click in the form itself — a feature-unlock threshold
      // not being met is just another shape of insufficient credits.
      if (err.code === 'INSUFFICIENT_CREDITS') {
        setCreditsBlockedMsg(err.message)
      } else {
        showToast(err.message || 'Failed to create exam. Please try again.', 'error')
      }
    } finally {
      setSaving(false)
    }
  }

  async function handleDeleteExam(id) {
    try {
      await api.delete(`/api/exams/${id}`)
      refetchExams()
      showToast(S.toastExamDeleted)
    } catch (err) { showToast(err.message, 'error') }
  }

  function handleUpdateExam(updated) {
    if (updated._deleted) {
      setExams(prev => prev.filter(e => e.id !== updated.id))
      setSelected(null)
      showToast(S.toastExamDeleted)
    } else {
      setExams(prev => prev.map(e => e.id === updated.id ? { ...e, ...updated } : e))
      setSelected(prev => prev ? { ...prev, ...updated } : null)
    }
  }


  const examTotalPages = examMeta.pages
  const examPageSafe   = examPage

  if (selectedExam) {
    return (
      <div className="w-full px-6 sm:px-40 py-6 sm:py-10">
        <Toast toast={toast} />
        <ExamDetail
          exam={selectedExam}
          onBack={() => setSelected(null)}
          onExamUpdate={handleUpdateExam}
          showToast={showToast}
        />
      </div>
    )
  }

  return (
    <div className="w-full px-6 sm:px-40 py-6 sm:py-10 animate-fade-up">
      <Toast toast={toast} />

      {creditsBlockedMsg && (
        <ConfirmModal
          title="Insufficient Credit Balance"
          message={creditsBlockedMsg}
          confirmLabel="Buy Credits"
          cancelLabel="Close"
          danger
          confirmStyle={{ background: 'var(--brand)', color: '#fff', border: 'none' }}
          onConfirm={() => { setCreditsBlockedMsg(null); setBuyCreditsOpen(true) }}
          onCancel={() => setCreditsBlockedMsg(null)}
        />
      )}
      {buyCreditsOpen && (
        <BuyCreditsModal user={user} updateUser={updateUser} showToast={showToast} onClose={() => setBuyCreditsOpen(false)} />
      )}

      {/* Header */}
      {/* Tabs */}
      <div className="flex items-center gap-1 border-b mb-6" style={{ borderColor: 'var(--border)' }}>
        {[{ key: 'exams', label: 'Exams' }, { key: 'users', label: 'Examinees' }].map(({ key, label }) => (
          <button key={key} onClick={() => { setTab(key); setCreating(false) }}
            className="px-4 py-2 text-sm font-medium transition-all relative"
            style={{ color: tab === key ? 'var(--brand)' : 'var(--text-3)' }}>
            {label}
            {tab === key && (
              <span className="absolute bottom-0 left-0 right-0 h-0.5 rounded-t" style={{ background: 'var(--brand)' }} />
            )}
          </button>
        ))}
      </div>

      {/* Users tab */}
      {tab === 'users' && (
        <>
          <div className="mb-5">
            <InviteCodeChip code={user?.inviteCode} orgName={user?.name} />
          </div>
          <UsersView adminId={user._id} showToast={showToast} />
        </>
      )}

      {tab === 'exams' && <>
      {/* Exam tab header */}
      {!creating && (
        <div className="flex items-center justify-between mb-5">
          <p className="text-xs text-[var(--text-3)]">{S.subtitle}</p>
          {exams.length > 0 && (
            <button onClick={handleNewExamClick} className="btn-primary flex-shrink-0 text-sm">{S.newExam}</button>
          )}
        </div>
      )}
      {creating && (
        <CreateExamForm onSubmit={handleCreate} onCancel={() => setCreating(false)} saving={saving} showToast={showToast} />
      )}

      {/* Search + Sort */}
      {!loading && (examMeta.total > 0 || debouncedQ) && (
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="relative w-full sm:w-56">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-3)] text-sm pointer-events-none">🔍</span>
            <input
              type="text"
              value={search}
              onChange={e => { setSearch(e.target.value); setExamPage(1) }}
              placeholder="Search..."
              className="w-full pl-9 pr-4 py-2 rounded-xl text-sm bg-[var(--surface)] border border-[var(--border)] text-[var(--text-1)] placeholder:text-[var(--text-3)] focus:outline-none focus:border-[var(--brand)] transition"
            />
            {search && (
              <button onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-3)] hover:text-[var(--text-1)] text-xs transition">✗</button>
            )}
          </div>
          <select
            value={sort}
            onChange={e => setSort(e.target.value)}
            className="px-3 py-2 rounded-xl text-sm bg-[var(--surface)] border border-[var(--border)] text-[var(--text-2)] focus:outline-none focus:border-[var(--brand)] transition cursor-pointer"
          >
            {SORT_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <span className="text-xs flex-shrink-0" style={{ color: 'var(--text-3)' }}>
            {examMeta.total} result{examMeta.total !== 1 ? 's' : ''}
          </span>
          <div className="flex items-center gap-2 ml-auto">
            <span className="text-xs" style={{ color: 'var(--text-3)' }}>Rows per page</span>
            <select value={examPerPage} onChange={e => { setExamPerPage(Number(e.target.value)); setExamPage(1) }}
              className="text-xs rounded-lg px-2 py-1.5 outline-none cursor-pointer"
              style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-1)' }}>
              {[5, 10, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
        </div>
      )}

      {/* Exam list */}
      {loading ? (
        <div className="card p-6 text-sm text-[var(--text-3)]">{S.loading}</div>
      ) : examMeta.total === 0 && !debouncedQ && !creating ? (
        <div className="card p-10 text-center">
          <p className="text-4xl mb-3">📚</p>
          <p className="text-base font-semibold text-[var(--text-1)] mb-1">{S.noExamsTitle}</p>
          <p className="text-sm text-[var(--text-3)] mb-4">{S.noExamsDesc}</p>
          <button onClick={handleNewExamClick} className="btn-primary text-sm">{S.createFirst}</button>
        </div>
      ) : exams.length === 0 && !creating ? (
        <div className="card p-8 text-center">
          <p className="text-2xl mb-2">🔍</p>
          <p className="text-sm font-semibold text-[var(--text-1)] mb-1">{S.noSearchResults(search)}</p>
          <button onClick={() => setSearch('')} className="text-xs text-[var(--brand)] hover:underline mt-1">{S.clearSearch}</button>
        </div>
      ) : (
        <>
          <div className="card overflow-hidden">
            {/* Table header */}
            <div className="hidden sm:grid px-6 py-3.5 text-xs font-bold uppercase tracking-widest border-b"
                 style={{ gridTemplateColumns: '2fr 100px 1.2fr 0.9fr 90px 110px', color: 'var(--text-3)', borderColor: 'var(--border)', background: 'var(--surface-2)' }}>
              <span>Exam</span>
              <span>Code</span>
              <span>Validity</span>
              <span>{S.statCreated}</span>
              <span>Status</span>
              <span className="text-center">Actions</span>
            </div>
            {exams.map(exam => (
              <ExamRow key={exam.id} exam={exam} onOpen={e => setSelected(e)} onDelete={handleDeleteExam} orgName={user?.name} />
            ))}
          </div>

          {examTotalPages > 1 && (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-5">
              <p className="text-xs" style={{ color: 'var(--text-3)' }}>
                Showing {(examPageSafe - 1) * examPerPage + 1}–{Math.min(examPageSafe * examPerPage, examMeta.total)} of {examMeta.total}
              </p>
              <div className="flex flex-wrap items-center gap-1">
                <button onClick={() => setExamPage(p => Math.max(1, p - 1))} disabled={examPageSafe === 1}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all disabled:opacity-30"
                  style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-2)' }}>← Prev</button>
                {Array.from({ length: examTotalPages }, (_, i) => i + 1).map(n => (
                  <button key={n} onClick={() => setExamPage(n)}
                    className="w-8 h-8 rounded-lg text-xs font-semibold transition-all"
                    style={n === examPageSafe
                      ? { background: 'var(--brand)', color: '#fff' }
                      : { background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-2)' }}>
                    {n}
                  </button>
                ))}
                <button onClick={() => setExamPage(p => Math.min(examTotalPages, p + 1))} disabled={examPageSafe === examTotalPages}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all disabled:opacity-30"
                  style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-2)' }}>Next →</button>
              </div>
            </div>
          )}
        </>
      )}
      </>}
    </div>
  )
}
